import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FeedbackService } from '../../services/feedback-service/feedback.service';
import { environment } from '../../../environments/environment';
import { UserService } from '../../services/user-service/user.service';

interface NeedHelpData {
  category: string;
  page: string;
  message: string;
}

@Component({
  selector: 'need-help-modal',
  templateUrl: './need-help-modal.html',
  styleUrl: './need-help-modal.scss',
})
export class NeedHelpModal {
  private feedbackService = inject(FeedbackService);
  userService = inject(UserService);

  userDetails = this.userService.getUserDetails();

  open = input(false);
  closeModal = output<void>();

  readonly categories = [
    'Feedback on experience',
    'Request for more assets',
    'Report issues with asset',
    'Others',
  ];

  selectedCategory = signal('');
  message = signal('');
  submitted = signal(false);
  submitting = signal(false);
  includePageContext = signal(true);

  canSubmit = computed(
    () => !!this.selectedCategory() && this.message().trim().length > 0 && !this.submitting(),
  );

  get currentPage(): string {
    return window.location.pathname + window.location.search + window.location.hash;
  }

  onOverlayClick(e: MouseEvent) {
    if ((e.target as HTMLElement).classList.contains('nh-overlay')) {
      this.reset();
    }
  }

  close() {
    this.closeModal.emit();
  }

  submit() {
    if (!this.canSubmit()) return;

    const needHelpData: NeedHelpData = {
      category: this.selectedCategory(),
      page: this.includePageContext() ? this.currentPage : '',
      message: this.message(),
    };

    this.submitting.set(true);
    this.feedbackService.submit(needHelpData).subscribe({
      next: () => {
        this.submitted.set(true);
        this.submitting.set(false);
        this.generateNeedHelpMailto(needHelpData);
      },
      error: () => {
        this.submitting.set(false);
      },
    });
  }

  reset() {
    this.selectedCategory.set('');
    this.message.set('');
    this.submitted.set(false);
    this.submitting.set(false);
    this.includePageContext.set(true);
    this.close();
  }

  generateNeedHelpMailto = ({ page, category, message }: NeedHelpData) => {
    const user = this.userDetails.data();
    const name = encodeURIComponent(user?.displayName ?? '');
    const email = encodeURIComponent(user?.userPrincipalName ?? '');
    const link = `mailto:${environment.DATANEX_SUPPORT_EMAIL}?subject=User%20${name}%20%28${email}%29%20submitted%20feedback.&body=Category%3A%20${encodeURIComponent(category)}%0APage%3A%20${encodeURIComponent(page)}%0A%0AMessage%3A%0A%0A${encodeURIComponent(message)}`;

    window.location.href = link;
  };
}
